// src/components/HeroSection.tsx (or wherever yours lives)
import React from 'react';
import Slider from 'react-slick';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import './HeroSection.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// JPEG fallbacks (originals)
import jpg1 from '../../images/HeroSection/1.jpg';
import jpg2 from '../../images/HeroSection/2.jpg';
import jpg3 from '../../images/HeroSection/3.jpg';
import jpg4 from '../../images/HeroSection/4.jpg';
import jpg5 from '../../images/HeroSection/5.jpg';
import jpg6 from '../../images/HeroSection/6.jpg';
import jpg7 from '../../images/HeroSection/7.jpg';
import jpg8 from '../../images/HeroSection/8.jpg';
import jpg9 from '../../images/HeroSection/9.jpg';
import jpg10 from '../../images/HeroSection/10.jpg';
import jpg11 from '../../images/HeroSection/11.jpg';
import jpg12 from '../../images/HeroSection/12.jpg';

// AVIF (only where you created them)
import avif1_1280 from '../../images/HeroSection/1-1280.avif';
import avif3_1280 from '../../images/HeroSection/3-1280.avif';
import avif3_1920 from '../../images/HeroSection/3-1920.avif';
import avif4_1280 from '../../images/HeroSection/4-1280.avif';
import avif5_1280 from '../../images/HeroSection/5-1280.avif';
import avif6_1280 from '../../images/HeroSection/6-1280.avif';
import avif8_1280 from '../../images/HeroSection/8-1280.avif';
import avif8_1920 from '../../images/HeroSection/8-1920.avif';
import avif9_1280 from '../../images/HeroSection/9-1280.avif';
import avif10_1280 from '../../images/HeroSection/10-1280.avif';
import avif11_1280 from '../../images/HeroSection/11-1280.avif';

// WebP (only where you created them)
import webp1_1280 from '../../images/HeroSection/1-1280.webp';
import webp3_1280 from '../../images/HeroSection/3-1280.webp';
import webp3_1920 from '../../images/HeroSection/3-1920.webp';
import webp4_1280 from '../../images/HeroSection/4-1280.webp';
import webp5_1280 from '../../images/HeroSection/5-1280.webp';
import webp6_1280 from '../../images/HeroSection/6-1280.webp';
import webp8_1280 from '../../images/HeroSection/8-1280.webp';
import webp8_1920 from '../../images/HeroSection/8-1920.webp';
import webp9_1280 from '../../images/HeroSection/9-1280.webp';
import webp10_1280 from '../../images/HeroSection/10-1280.webp';
import webp11_1280 from '../../images/HeroSection/11-1280.webp';

// Describe each slide and which variants exist
type Slide = {
  alt: string;
  jpg: string;
  avif1280?: string;
  avif1920?: string;
  webp1280?: string;
  webp1920?: string;
};

const slides: Slide[] = [
  { alt: 'Hero 1',  jpg: jpg1,  avif1280: avif1_1280,  webp1280: webp1_1280 },
  { alt: 'Hero 2',  jpg: jpg2 }, // original <1280: no upscaling, jpg only
  { alt: 'Hero 3',  jpg: jpg3,  avif1280: avif3_1280, avif1920: avif3_1920, webp1280: webp3_1280, webp1920: webp3_1920 },
  { alt: 'Hero 4',  jpg: jpg4,  avif1280: avif4_1280,  webp1280: webp4_1280 },
  { alt: 'Hero 5',  jpg: jpg5,  avif1280: avif5_1280,  webp1280: webp5_1280 },
  { alt: 'Hero 6',  jpg: jpg6,  avif1280: avif6_1280,  webp1280: webp6_1280 },
  { alt: 'Hero 7',  jpg: jpg7 }, // original <1280
  { alt: 'Hero 8',  jpg: jpg8,  avif1280: avif8_1280, avif1920: avif8_1920, webp1280: webp8_1280, webp1920: webp8_1920 },
  { alt: 'Hero 9',  jpg: jpg9,  avif1280: avif9_1280,  webp1280: webp9_1280 },
  { alt: 'Hero 10', jpg: jpg10, avif1280: avif10_1280, webp1280: webp10_1280 },
  { alt: 'Hero 11', jpg: jpg11, avif1280: avif11_1280, webp1280: webp11_1280 },
  { alt: 'Hero 12', jpg: jpg12 }, // original <1280
];

const HeroSection = () => {
  const { t } = useTranslation();

  const imageSettings = {
    dots: false,
    infinite: true,
    fade: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    arrows: false,
    draggable: false,
    swipe: false,
    touchMove: false,
    pauseOnHover: false,

    // ✅ only load slides when needed
    lazyLoad: 'ondemand' as const, // react-slick API supports 'ondemand' | 'progressive'
  };

  return (
    <Box className="hero-section">
      <Slider {...imageSettings}>
        {slides.map((s, i) => {
          const isFirst = i === 0;

          // Build srcsets based on what exists for the slide
          const avifSet =
            s.avif1920 && s.avif1280
              ? `${s.avif1280} 1280w, ${s.avif1920} 1920w`
              : s.avif1280
              ? `${s.avif1280} 1280w`
              : undefined;

          const webpSet =
            s.webp1920 && s.webp1280
              ? `${s.webp1280} 1280w, ${s.webp1920} 1920w`
              : s.webp1280
              ? `${s.webp1280} 1280w`
              : undefined;

          return (
            <div key={i}>
              <picture>
                {/* Prefer AVIF, then WebP */}
                {avifSet && <source type="image/avif" srcSet={avifSet} sizes="100vw" />}
                {webpSet && <source type="image/webp" srcSet={webpSet} sizes="100vw" />}

                {/* JPEG fallback (original). Width/height give layout a stable ratio; CSS still uses object-fit: cover */}
                <img
                  src={s.jpg}
                  alt={s.alt}
                  className="hero-section-image"
                  sizes="100vw"
                  loading={isFirst ? 'eager' : 'lazy'}
                  // MDN: fetchpriority/fetchPriority hints download order
                  // Only the first (likely LCP) should be high priority
                  {...(isFirst
                    ? { fetchPriority: 'high' as const }
                    : { fetchPriority: 'low' as const })}
                  decoding="async"
                  width={1920}
                  height={1080}
                />
              </picture>
            </div>
          );
        })}
      </Slider>

      <Box className="hero-section-overlay">
        <Box className="hero-section-text-container">
          <Box className="hero-section-content">
            <div className="hero-section-title-strip" />
            <Typography className="hero-section-title">
              {t('heroSection.title')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default HeroSection;
